import Term from "@/components/Term";

/* Evergreen shastra constants only — period lengths are fixed Vimshottari values
   (rule 24: no person-specific dates/durations are ever rendered here). */
const MAHADASHA_MEANINGS: { lord: string; years: number; meaning: string }[] = [
  {
    lord: "Ketu",
    years: 7,
    meaning:
      "Ketu Mahadasha turns life inward — spirituality, detachment, and old karmic patterns come to the surface. If you feel strangely disconnected from goals that once mattered, tradition says this is Ketu loosening your grip on the material so the inner path can open. Simple upay like seva and meditation are classically recommended during this period.",
  },
  {
    lord: "Venus",
    years: 20,
    meaning:
      "Shukra (Venus) Mahadasha is the longest period at 20 years and rules love, marriage, comfort, arts, and wealth. A well-placed Venus brings relationships, vehicles, and material ease; an afflicted Venus is the classic explanation for why relationships feel pleasant on the surface yet never fully settle.",
  },
  {
    lord: "Sun",
    years: 6,
    meaning:
      "Surya (Sun) Mahadasha activates authority, career, government matters, and the relationship with the father. It is short but intense — recognition and leadership can rise quickly, and so can ego clashes with seniors. Jyotish texts read a difficult Sun period as the reason respect feels hard-won despite genuine ability.",
  },
  {
    lord: "Moon",
    years: 10,
    meaning:
      "Chandra (Moon) Mahadasha rules the mind, emotions, mother, and public life. When the Moon is strong, mental peace and popularity flow; when afflicted, this is the period tradition points to when emotions swing without an obvious cause and sleep or restlessness becomes a pattern.",
  },
  {
    lord: "Mars",
    years: 7,
    meaning:
      "Mangal (Mars) Mahadasha brings raw energy — courage, property, siblings, and physical drive. Careers in engineering, defence, and sports classically flourish. The classic worry it explains: a temper that flares even when you do not want it to, and conflicts that seem to find you.",
  },
  {
    lord: "Rahu",
    years: 18,
    meaning:
      "Rahu Mahadasha is 18 years of worldly ambition — foreign lands, technology, unconventional success, and sudden rises. Its shadow side is the mind that will not switch off: obsessive loops, restlessness, and desires that grow the moment they are fulfilled. Tradition treats this period as transformative, not cursed.",
  },
  {
    lord: "Jupiter",
    years: 16,
    meaning:
      "Guru (Jupiter) Mahadasha is generally the most auspicious period — wisdom, children, education, dharma, and wealth expand through righteous means. Teachers and mentors appear. Even here, an afflicted Jupiter is the traditional explanation for good judgment arriving late, after the decision is already made.",
  },
  {
    lord: "Saturn",
    years: 19,
    meaning:
      "Shani (Saturn) Mahadasha is the great teacher — 19 years of discipline, karma, and slow, earned results. It is the classic answer to “why does my hard work never turn into results?”: Shani delays but does not deny, rewarding sustained effort with stability that no other period can give.",
  },
  {
    lord: "Mercury",
    years: 17,
    meaning:
      "Budh (Mercury) Mahadasha sharpens intellect, communication, business, and trade. Writers, analysts, and entrepreneurs classically thrive. When Budh is weak or afflicted, this is the period tradition names for scattered focus — many ideas started, few finished.",
  },
];

export function MahadashaMeanings() {
  return (
    <div className="cv-auto [contain-intrinsic-size:auto_2400px]">
      <h2
        className="text-xl font-bold mb-4 mt-10"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        What Each Mahadasha Period Means
      </h2>
      <p className="text-[14px] leading-relaxed mb-6">
        Every <Term k="mahadasha">Mahadasha</Term> activates one planet&apos;s significations as the
        dominant theme of life. The period lengths below are fixed Vimshottari values from the
        classical texts; how each period actually unfolds depends on that planet&apos;s strength and
        placement in your kundali. The chart shows tendencies, not fixed outcomes.
      </p>
      <div className="space-y-5 mb-6">
        {MAHADASHA_MEANINGS.map((m) => (
          <div key={m.lord}>
            <h3 className="text-[15px] font-bold mb-1" style={{ color: "var(--ish-dark)" }}>
              {m.lord} Mahadasha &mdash; {m.years} Years
            </h3>
            <p className="text-[14px] leading-relaxed">{m.meaning}</p>
          </div>
        ))}
      </div>

      <h2
        className="text-xl font-bold mb-4 mt-10"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        What Changes When Your Antardasha Shifts
      </h2>
      <p className="text-[14px] leading-relaxed mb-4">
        Within every Mahadasha, the <Term k="antardasha">Antardasha</Term> (Bhukti) lord changes
        every few months to a few years, and with it the texture of the period changes. The
        Mahadasha lord stays the landlord; the Antardasha lord is the tenant who decides how the
        house feels day to day. This is why the same Shani Mahadasha can feel crushing in one phase
        and quietly productive in the next.
      </p>
      <p className="text-[14px] leading-relaxed mb-6">
        When an Antardasha shifts, Jyotish tradition expects three things to move: the life areas in
        focus (the new sub-lord&apos;s houses activate), the pace of results (friendly
        Mahadasha&ndash;Antardasha pairs ease delivery, inimical pairs create friction), and the
        emotional tone. If life changed noticeably in recent months with no outer cause, check
        whether your Antardasha changed &mdash; that transition, not a single event, is often the
        traditional explanation.
      </p>
    </div>
  );
}
